import React, { useEffect, useRef, useState } from 'react';

import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { getEmptyMandatoryOrFieldsWithError } from 'apps/auth/components/form-multi/utils';
import { KYCModal } from 'apps/auth/components/kyc/KYCModal';
import KycOfakimDocAddPhotoPage from 'apps/auth/components/profile/ofakim/KycOfakimDocAddPhotoPage';
import KycOfakimDocNumberModal from 'apps/auth/components/profile/ofakim/KycOfakimDocNumberModal';
import KycOfakimDocsList from 'apps/auth/components/profile/ofakim/KycOfakimDocsList';
import KycOfakimStatus from 'apps/auth/components/profile/ofakim/KycOfakimStatus';
import ProfileFieldsEditModal from 'apps/auth/components/profile/ofakim/ProfileFieldsEditModal';
import { OFAKIM_FORM_NAME, PROFILE_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_PROFILE_FIELDS_UPDATE } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { useExtra, useForm, usePageMessages } from 'apps/auth/hooks/selectors';

const ofakimModalsList = {
    KYCModal: 'KYCModal',
    DocsList: 'DocsList',
    AddPhotoPage: 'AddPhotoPage',
    DocNumber: 'DocNumber',
    OfakimStatus: 'OfakimStatus',
    ProfileFieldsEdit: 'ProfileFieldsEdit',
};

const screenOrder = {
    first: 'first_screen',
    second: 'second_screen',
};

export const MainOfakimModal = (props) => {
    const { addExtras, updatePageMessages, toggleSpinner, setNewForm, onClickNotNow, onClickOfakimStatus, showIntro } = props;

    const { docs, userCountry, form, status, profile_form, ofakimFlowUpdateProfile, phoneCountry } = useExtra();
    const ofakimForm = useForm(OFAKIM_FORM_NAME);
    const profileForm = useForm(PROFILE_FORM_NAME);
    const {
        title,
        buttonContinue,
        docNumberTitle,
        profilePurpleBlock,
        aml_level_name,
        increase_block,
        increase_sub_block,
        notificationButton,
        nextButton,
        gotItButton,
        screenTitle,
        screenText,
        subTitle,
        addPhotoPageTitle,
        profileHeaderTitle,
        headerGrayTitle,
    } = usePageMessages();

    const [currentModal, setCurrentModal] = useState(
        showIntro ? ofakimModalsList.KYCModal : ofakimFlowUpdateProfile ? ofakimModalsList.ProfileFieldsEdit : ofakimModalsList.DocsList,
    );
    const submitRef = useRef(null);

    const ofakimFields = ofakimForm?.fields;
    const ofakimAction = ofakimForm?.action;
    const profilesFields = profileForm?.fields;

    useEffect(() => {
        if (form?.fields && currentModal === ofakimModalsList.DocsList) {
            setNewForm(OFAKIM_FORM_NAME, form?.fields, `/auth/kyc/gt/ofakim/doc/${userCountry.id}/${form.id}/upload.action?validation=true`);
        }
    }, [currentModal, form]);

    useEffect(() => {
        if (profile_form && setNewForm) {
            const formFields = profile_form.fields.reduce((acc, el) => {
                acc[el.name] = el;
                return acc;
            }, {});
            setNewForm(PROFILE_FORM_NAME, formFields, AUTH_PROFILE_FIELDS_UPDATE);
        }
    }, [profile_form]);

    const submitFields = (fields) => {
        const fieldsArray = Array.isArray(fields) ? fields : Object.values(fields);
        fieldsArray.forEach((field) => {
            if (field.destination !== screenOrder.first) {
                field.value = '';
            }
        });
    };

    const addPhotoBeforeSubmit = () => submitFields(ofakimFields);

    const addPhotoSubmitHandler = (fields) => {
        submitFields(fields);
        setCurrentModal(ofakimModalsList.DocNumber);
    };

    const submitOfakimNumberHandler = (fields, json) => {
        setCurrentModal(ofakimModalsList.OfakimStatus);
        if (json.extra) {
            addExtras(json.extra);
        }
    };

    const profileFieldsSubmitHandler = (fields, json) => {
        const form = json?.forms[PROFILE_FORM_NAME];
        if (form) {
            setNewForm(PROFILE_FORM_NAME, form.fields, form.action);
        }
        setCurrentModal(ofakimModalsList.DocsList);
    };

    const isEnabled = (fields) => {
        return getEmptyMandatoryOrFieldsWithError(fields, false, true).length === 0;
    };

    const getScreenFields = (fields, order) => {
        const visibleFields = [];
        const fieldsArray = Array.isArray(fields) ? fields : Object.values(fields);

        fieldsArray.forEach((field) => {
            if (field.destination === order) {
                field.visible = true;
                visibleFields.push(field);
            } else {
                field.visible = false;
            }
        });
        return visibleFields;
    };

    const getFirstScreenFields = (fields) => getScreenFields(fields, screenOrder.first);

    const getSecondScreenFields = (fields) => getScreenFields(fields, screenOrder.second);

    const ofakimStatusRenderText = () =>
        [screenText, subTitle].map((paragraph, index) => {
            if (paragraph) {
                return <DangerouslyHtml dangerouslyHtml={paragraph} key={`${index}paragraphText`} />;
            }
            return null;
        });

    const docListOnClick = (e, elem) => {
        e.preventDefault();
        apiFetchData(`/auth/kyc/gt/ofakim/doc/${userCountry.id}/${elem.id}`)
            .then((json) => {
                if (json && json.redirectTo) {
                    window.location.replace(json.redirectTo);
                } else {
                    toggleSpinner(true)
                        .then(() => addExtras(json.extra))
                        .then(() => updatePageMessages(json.messages))
                        .then(() => setCurrentModal(ofakimModalsList.AddPhotoPage))
                        .finally(() => toggleSpinner(false));
                }
            })
            .catch((error) => {
                logWarn(error);
                toggleSpinner(false);
            });
    };

    const getModalBody = () => {
        switch (currentModal) {
            case ofakimModalsList.KYCModal: {
                const onClickContinue = () =>
                    ofakimFlowUpdateProfile ? setCurrentModal(ofakimModalsList.ProfileFieldsEdit) : setCurrentModal(ofakimModalsList.DocsList);

                return (
                    <KYCModal
                        profilePurpleBlock={profilePurpleBlock}
                        aml_level_name={aml_level_name}
                        increase_block={increase_block}
                        increase_sub_block={increase_sub_block}
                        notificationButton={notificationButton}
                        nextButton={nextButton}
                        onClickContinue={onClickContinue}
                        onClickNotNow={onClickNotNow}
                    />
                );
            }
            case ofakimModalsList.DocsList:
                return (
                    <KycOfakimDocsList title={title} docs={docs} onCloseClick={(e) => onClickNotNow(e)} userCountry={userCountry} onClick={docListOnClick} />
                );

            case ofakimModalsList.AddPhotoPage:
                return (
                    <KycOfakimDocAddPhotoPage
                        title={addPhotoPageTitle}
                        onCloseClick={(e) => {
                            if (props.preventCloseNavigation) {
                                e?.preventDefault();
                            }
                            setCurrentModal(ofakimModalsList.DocsList);
                        }}
                        beforeSubmit={addPhotoBeforeSubmit}
                        submitHandler={addPhotoSubmitHandler}
                        getFirstScreenFields={getFirstScreenFields}
                        ofakimFields={ofakimFields}
                        isEnabled={isEnabled}
                        submitRef={submitRef}
                        buttonContinue={buttonContinue}
                    />
                );

            case ofakimModalsList.DocNumber:
                return (
                    <KycOfakimDocNumberModal
                        onCloseClick={(e) => {
                            if (props.preventCloseNavigation) {
                                e?.preventDefault();
                            }
                            setCurrentModal(ofakimModalsList.AddPhotoPage);
                        }}
                        title={docNumberTitle}
                        action={ofakimAction}
                        getSecondScreenFields={getSecondScreenFields}
                        fields={ofakimFields}
                        submitRef={submitRef}
                        buttonContinue={buttonContinue}
                        isEnabled={isEnabled}
                        onSubmitHandler={submitOfakimNumberHandler}
                    />
                );

            case ofakimModalsList.OfakimStatus:
                return (
                    <KycOfakimStatus
                        status={status}
                        screenTitle={screenTitle}
                        renderText={ofakimStatusRenderText}
                        gotItButton={gotItButton}
                        onClick={onClickOfakimStatus}
                    />
                );

            case ofakimModalsList.ProfileFieldsEdit:
                return (
                    <ProfileFieldsEditModal
                        onCloseIconClick={(e) => (showIntro ? setCurrentModal(ofakimModalsList.KYCModal) : onClickNotNow(e))}
                        headerTitle={profileHeaderTitle}
                        headerGrayTitle={headerGrayTitle}
                        profilesFields={profilesFields}
                        profileForm={profile_form}
                        phoneCountry={phoneCountry}
                        buttonContinue={buttonContinue}
                        onSubmitHandler={profileFieldsSubmitHandler}
                    />
                );
            default:
                return null;
        }
    };

    return getModalBody();
};

export default MainOfakimModal;
